import { useEffect, useRef, useState } from "react";
import { Lock, Mic } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAudioRecorder } from "react-audio-voice-recorder";
import { useEntitlements } from "@/hooks/useEntitlements";
import LockedFeatureSheet from "@/components/LockedFeatureSheet";
import { useActiveSpace } from "@/hooks/useActiveSpace";
import { useAiProcessingGate } from "@/hooks/useAiProcessingGate";
import { useIsChildRole } from "@/hooks/useIsChildRole";

export interface ContextResult {
  contextBlob?: Blob;
  contextText?: string;
  approvalStatus?: 'pending' | 'private';
}

interface ContextInputPanelProps {
  onProcess: (ctx: ContextResult) => void;
  onSaveLater?: () => void;
  accentColor?: string;
}

type Mode = "voice" | "written" | "skip";

function fmt(s: number) {
  return `${Math.floor(s / 60)}:${String(Math.floor(s) % 60).padStart(2, "0")}`;
}

export default function ContextInputPanel({ onProcess, onSaveLater, accentColor }: ContextInputPanelProps) {
  const { t } = useTranslation();
  const { canVoiceContext } = useEntitlements();
  const { space: activeSpace } = useActiveSpace();
  const { aiEnabled } = useAiProcessingGate(activeSpace?.id ?? null);
  const { isChild } = useIsChildRole();
  // Free tiers (seedling/ember) default to the Write tab — the Voice tab is
  // rendered but locked, tapping it opens LockedFeatureSheet.
  const [mode, setMode] = useState<Mode>(canVoiceContext ? "voice" : "written");
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedDuration, setCapturedDuration] = useState(0);
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [lockedSheetOpen, setLockedSheetOpen] = useState(false);
  const shouldCaptureRef = useRef(false);
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'private'>('pending');

  const { startRecording, stopRecording, recordingBlob, recordingTime } =
    useAudioRecorder(undefined, () => {});

  useEffect(() => {
    if (!recordingBlob || !shouldCaptureRef.current) return;
    shouldCaptureRef.current = false;
    setCapturedBlob(recordingBlob);
    setCapturedDuration(recordingTime);
    setRecording(false);
  }, [recordingBlob]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartRecording = () => {
    setCapturedBlob(null);
    setRecording(true);
    startRecording();
  };

  const handleStopRecording = () => {
    shouldCaptureRef.current = true;
    stopRecording();
  };

  const canProcess =
    mode === "skip" ||
    (mode === "voice" && capturedBlob !== null) ||
    (mode === "written" && text.trim().length > 0);

  const handleProcess = () => {
    if (!canProcess) return;
    const privacy = isChild ? { approvalStatus } : {};
    if (mode === "skip") onProcess({ ...privacy });
    else if (mode === "voice") onProcess({ contextBlob: capturedBlob ?? undefined, ...privacy });
    else onProcess({ contextText: text.trim(), ...privacy });
  };

  const btnStyle = accentColor && canProcess ? { backgroundColor: accentColor, color: "#FFFFFF" } : undefined;

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Intro — explains what this step is for */}
      <div className="flex flex-col gap-1">
        <p className="text-[15px] font-semibold text-foreground">
          {t("capture.context.heading")}
        </p>
        <p className="text-[13px] text-muted-foreground leading-snug">
          {t("capture.context.subtitle")}
        </p>
      </div>

      {/* AI-off notice — voice/written context still saves, but voice
          won't be transcribed and Mori won't write a story. */}
      {!aiEnabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] leading-snug text-amber-900">
          <span className="font-semibold">🪄 Mori's magic is off in this space.</span>{" "}
          {t("capture.context.aiOffHint")}
        </div>
      )}

      {/* Three-tab selector. Voice tab is locked on free tiers — tapping
          it opens LockedFeatureSheet rather than switching mode. */}
      <div className="flex items-center rounded-xl bg-muted p-1 gap-0.5">
        {(["voice", "written", "skip"] as Mode[]).map((m) => {
          const locked = m === "voice" && !canVoiceContext;
          return (
            <button
              key={m}
              type="button"
              onClick={() => (locked ? setLockedSheetOpen(true) : setMode(m))}
              className={`flex-1 py-2 text-[13px] font-medium rounded-lg transition-all ${
                mode === m && !locked
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              } ${locked ? "opacity-50" : ""}`}
            >
              {m === "voice" ? (
                <span className="inline-flex items-center gap-1">
                  🎤 Voice
                  {locked && <Lock className="h-3 w-3" aria-hidden="true" />}
                </span>
              ) : m === "written" ? (
                "✍️ Write"
              ) : (
                "Skip →"
              )}
            </button>
          );
        })}
      </div>

      <LockedFeatureSheet
        feature="voice_context"
        open={lockedSheetOpen}
        onClose={() => setLockedSheetOpen(false)}
      />

      {/* Voice mode */}
      {mode === "voice" && (
        <div className="flex flex-col items-center gap-3 py-2">
          {recording ? (
            <>
              <div className="relative flex items-center justify-center">
                <div className="absolute h-24 w-24 rounded-full bg-primary/10 animate-recording-pulse" />
                <div
                  className="absolute h-16 w-16 rounded-full bg-primary/15 animate-recording-pulse"
                  style={{ animationDelay: "0.5s" }}
                />
                <button
                  type="button"
                  onClick={handleStopRecording}
                  className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary"
                  style={accentColor ? { backgroundColor: accentColor } : undefined}
                >
                  <Mic className="h-6 w-6 text-primary-foreground" />
                </button>
              </div>
              <p className="text-2xl font-semibold tabular-nums text-foreground">{fmt(recordingTime)}</p>
              <p className="text-xs text-muted-foreground">{t("capture.context.tapMicToStop")}</p>
            </>
          ) : capturedBlob ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10"
                style={accentColor ? { backgroundColor: `${accentColor}20` } : undefined}
              >
                <Mic className="h-6 w-6 text-primary" style={accentColor ? { color: accentColor } : undefined} />
              </div>
              <p className="text-sm font-medium text-foreground">{t("capture.context.recorded", { duration: fmt(capturedDuration) })}</p>
              <button
                type="button"
                onClick={handleStartRecording}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("capture.context.reRecord")}
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={handleStartRecording}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-primary transition-transform active:scale-95 hover:scale-105"
                style={accentColor ? { backgroundColor: accentColor } : undefined}
              >
                <Mic className="h-7 w-7 text-primary-foreground" />
              </button>
              <p className="text-sm text-muted-foreground">{t("capture.context.tapToRecord")}</p>
            </>
          )}
        </div>
      )}

      {/* Written mode */}
      {mode === "written" && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("capture.context.writtenPlaceholder")}
          rows={4}
          autoFocus
          className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
        />
      )}

      {/* Skip mode */}
      {mode === "skip" && (
        <p className="text-sm text-muted-foreground text-center py-3">
          {t("capture.context.skipDescription")}
        </p>
      )}

      {/* Privacy toggle — child sessions only */}
      {isChild && (
        <div className="space-y-1.5">
          <p className="text-[13px] font-medium text-foreground">{t('capture.privacy.title')}</p>
          <div className="flex items-center rounded-xl bg-muted p-1 gap-0.5">
            {(['pending', 'private'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setApprovalStatus(v)}
                className={`flex-1 py-2 text-[13px] font-medium rounded-lg transition-all ${
                  approvalStatus === v
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {v === 'pending' ? t('capture.privacy.shareWithFamily') : t('capture.privacy.justForMe')}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t('capture.privacy.helper')}</p>
        </div>
      )}

      {/* Actions */}
      <Button
        onClick={handleProcess}
        disabled={!canProcess}
        className="w-full h-12 text-[15px] font-semibold rounded-2xl"
        style={btnStyle}
      >
        {t("capture.context.processButton")}
      </Button>
      {onSaveLater && (
        <Button
          variant="outline"
          onClick={onSaveLater}
          className="w-full h-12 text-[15px] font-semibold rounded-2xl"
        >
          {t("capture.context.saveLater")}
        </Button>
      )}
    </div>
  );
}
